import type {
  LabResultWithTerpene,
  PriceId,
  TerpeneFlavorEnum,
  WeightedLabResult,
} from '@iheartjane/types';
import { TerpeneEnumValues } from '@iheartjane/types';

import { TERPENE_FLAVORS } from './constants';

export const getTerpenesFromLabResults = (
  labResults: WeightedLabResult[],
  weight: PriceId
): LabResultWithTerpene[] => {
  const labResult = labResults?.find(({ price_id }) => price_id === weight);

  if (!labResult) {
    return [];
  }

  return labResult.lab_results
    .filter(({ value }) => (value || 0) > 0)
    .reduce(
      (
        prev: LabResultWithTerpene[],
        curr: WeightedLabResult['lab_results'][number]
      ) => {
        const compoundName = curr.compound_name.toLowerCase();

        const matchingTerpene = TerpeneEnumValues.find((terpene) =>
          compoundName.endsWith(terpene)
        );

        if (!matchingTerpene) {
          return prev;
        }

        return [...prev, { ...curr, matchingTerpene }];
      },
      []
    );
};

export const getTopFlavors = (
  labResults: WeightedLabResult[] | null,
  weight: PriceId
) => {
  if (!labResults) {
    return [];
  }

  const flavors: { [flavor: string]: number } = {};
  const terpenes = getTerpenesFromLabResults(labResults, weight);

  terpenes.forEach((terpene: LabResultWithTerpene) => {
    const flavor = TERPENE_FLAVORS[terpene.matchingTerpene];

    if (flavor) {
      flavors[flavor] = (flavors[flavor] || 0) + (terpene.value || 0);
    }
  });

  return Object.entries(flavors)
    .sort(([, valueA], [, valueB]) => valueB - valueA)
    .slice(0, 3)
    .map(([key, value]) => ({ count: value, value: key as TerpeneFlavorEnum }));
};

export const getSortedTerpenes = (
  labResults: WeightedLabResult[] | null,
  weight: PriceId
) => {
  if (!labResults) {
    return [];
  }

  return getTerpenesFromLabResults(labResults, weight).sort(
    ({ value: valueA }, { value: valueB }) => valueB - valueA
  );
};
